import PropTypes from "prop-types";
import React from "react";

import { useAppSetting, useMbox, usePageId } from "@swa-ui/application";
import { getBootstrapData } from "@swa-ui/bootstrap";
import { useDeviceInfo } from "@swa-ui/browser";
import {
  applyAllOverrides,
  applyOverrides,
  PlacementFactory,
  PlacementSection,
  usePlacement,
  usePlacementIds,
} from "@swa-ui/placements";

const COMPONENT_TYPE = "component";
const LAYOUT_TYPE_HEADING = "heading";
const PAGE_LAYOUT_PLACEMENT_ID = "pageLayout";

export const PageFactory = (props) => {
  const { componentRenderers, renderLoading, showBootstrap } = props;
  const { mbox: pageLayoutId } = useMbox("pageLayoutId", PAGE_LAYOUT_PLACEMENT_ID);
  const allPlacementIds = usePlacementIds(new RegExp("(.+)"));
  const enablePageLayoutCall = useAppSetting("enablePageLayoutCall", false);
  const hrefBasePath = useAppSetting("placementsHrefBasePath");
  const placementFromCall = usePlacement(pageLayoutId);
  const { screenSize } = useDeviceInfo();
  const { pageId } = usePageId();
  const { error, isComplete, pageLayoutPlacement } = getPageLayoutPlacement();

  return pageLayoutPlacement?.contentItems?.map(renderSection);

  function renderSection(item) {
    const { id, type } = item;
    const { fullWidth } = applyOverrides(item, screenSize);
    let Component;

    if (!id && !type) {
      Component = (
        <PlacementSection {...item} key={item?.heading?.value} layout={LAYOUT_TYPE_HEADING} />
      );
    } else if (type === COMPONENT_TYPE) {
      const Node = componentRenderers?.[id]?.();

      Component = !!Node && (
        <PlacementSection {...item} key={id}>
          {Node}
        </PlacementSection>
      );
    } else if (allPlacementIds.includes(id)) {
      Component = (
        <PlacementSection {...item} key={id}>
          <PlacementFactory {...getPlacementFactoryProps({ fullWidth, id })} />
        </PlacementSection>
      );
    } else if (!isComplete && !error) {
      Component = (
        <PlacementSection {...item} key={id}>
          {renderLoading?.()}
        </PlacementSection>
      );
    }

    return Component;
  }

  function getPlacementFactoryProps({ fullWidth, id }) {
    return {
      additionalProps: { fullWidth },
      componentRenderers,
      id,
      shouldWait: !showBootstrap,
    };
  }

  function getPageLayoutFromBootstrap() {
    const bootstrapData = getBootstrapData("bootstrap-content/en/content");
    let pageLayoutContent = bootstrapData[pageId]?.results?.[pageLayoutId]?.content;

    if (!pageLayoutContent) {
      pageLayoutContent = bootstrapData[pageId]?.results?.[PAGE_LAYOUT_PLACEMENT_ID]?.content;
    }

    return pageLayoutContent;
  }

  function getPageLayoutPlacement() {
    let placementError = null;
    let placementIsComplete = true;
    let finalPlacement = [];

    if (enablePageLayoutCall) {
      placementError = placementFromCall?.error;
      placementIsComplete = placementFromCall?.isComplete;
      finalPlacement = placementFromCall?.placement?.placement;
    } else {
      finalPlacement = applyAllOverrides({
        allPlacements: getPageLayoutFromBootstrap()?.placement,
        hrefBasePath,
        screenSize,
      });
    }

    return {
      error: placementError,
      isComplete: placementIsComplete,
      pageLayoutPlacement: finalPlacement,
    };
  }
};

PageFactory.defaultProps = {
  showBootstrap: true,
};

PageFactory.displayName = "PageFactory";
PageFactory.propTypes = {
  /** Callback functions with custom registered components. */
  componentRenderers: PropTypes.object,

  /** Function to run when placements are still loading. */
  renderLoading: PropTypes.func,

  /** Boolean to show or hide bootstrap content while placements are loading. */
  showBootstrap: PropTypes.bool,
};
